import React, { useMemo, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Button, VStack, HStack, Box, Text, Badge, Divider, useToast, Icon, SimpleGrid, Flex, Checkbox, Wrap, WrapItem,
} from '@chakra-ui/react';
import { FaExchangeAlt, FaArrowRight } from 'react-icons/fa';
import { mergeConflicts, mergeSubmissions, combineOrderIds } from '../../utils/mergeSubmissions';

const prettyField = (f) => f.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const showVal = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const s = String(v);
  return s.length > 80 ? s.slice(0, 80) + '…' : s;
};

// primary = the record kept; duplicate = the record merged away + cancelled.
export default function MergeSubmissionsModal({ isOpen, onClose, primary, duplicate, onMerged }) {
  const toast = useToast();
  const [swapped, setSwapped] = useState(false);
  const [resolved, setResolved] = useState({}); // field -> 'primary' | 'duplicate'
  const [saving, setSaving] = useState(false);

  const keep = swapped ? duplicate : primary;
  const drop = swapped ? primary : duplicate;

  const conflicts = useMemo(
    () => (keep && drop ? mergeConflicts(keep, drop) : []),
    [keep, drop]
  );

  // Tests on the script for each side, plus which of the duplicate's tests are
  // new (not already on the kept submission).
  const keepTests = useMemo(() => (Array.isArray(keep?.tests_on_the_script) ? keep.tests_on_the_script : []), [keep]);
  const dropTests = useMemo(() => (Array.isArray(drop?.tests_on_the_script) ? drop.tests_on_the_script : []), [drop]);
  // Key by id, falling back to name/code so LDX-imported tests (no id) are not
  // mistakenly treated as duplicates of each other.
  const testKey = (t) => (
    t?.id != null ? `id:${t.id}`
      : t?.name ? `nm:${String(t.name).trim().toLowerCase()}`
      : t?.code ? `cd:${String(t.code).trim().toLowerCase()}`
      : null
  );
  const newFromDrop = useMemo(() => {
    const seen = new Set(keepTests.map(testKey).filter(Boolean));
    const out = [];
    for (const t of dropTests) {
      if (!t) continue;
      const key = testKey(t);
      if (key && seen.has(key)) continue; // already on keep, or already counted
      if (key) seen.add(key);
      out.push(t);
    }
    return out;
  }, [keepTests, dropTests]); // eslint-disable-line react-hooks/exhaustive-deps
  const [mergeTests, setMergeTests] = useState(true);

  // Order IDs (lab_order_number) are always unioned onto the survivor for the
  // phleb + admin to see. Status pushes still route by ldx_message_id (one
  // survivor), so this is display-only. Show what the combined value will be.
  const combinedOrderIds = useMemo(() => combineOrderIds(keep?.lab_order_number, drop?.lab_order_number), [keep, drop]);
  const gainsOrderId = combinedOrderIds && combinedOrderIds !== (keep?.lab_order_number || '');

  const choose = (field, which) => setResolved((r) => ({ ...r, [field]: which }));

  const doMerge = async () => {
    setSaving(true);
    try {
      await mergeSubmissions({ primary: keep, duplicate: drop, resolved, mergeTests });
      toast({ title: `Merged #${drop.id} into #${keep.id}`, status: 'success', duration: 3000, isClosable: true });
      onClose();
      if (onMerged) onMerged({ keptId: keep.id, droppedId: drop.id });
    } catch (e) {
      toast({ title: 'Merge failed', description: e.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  if (!primary || !duplicate) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.500" />
      <ModalContent borderRadius="xl" mx={4}>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={FaExchangeAlt} color="purple.500" />
            <Text>Merge submissions</Text>
          </HStack>
          <HStack mt={2} spacing={2} fontSize="sm" color="gray.600">
            <Badge colorScheme="green">Keep #{keep.id}</Badge>
            <Icon as={FaArrowRight} boxSize={3} color="gray.400" />
            <Badge colorScheme="red">Cancel #{drop.id}</Badge>
            <Button size="xs" variant="ghost" leftIcon={<FaExchangeAlt />} onClick={() => setSwapped((s) => !s)}>
              Swap
            </Button>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Box bg="blue.50" borderRadius="md" p={3} fontSize="sm" color="blue.800">
              All status notes from <b>#{drop.id}</b> move onto <b>#{keep.id}</b>; tags &amp; document images are
              combined; blank fields on #{keep.id} are filled from #{drop.id}; lab links are re-pointed; and
              #{drop.id} is cancelled. Pick a winner below only for fields that conflict.
            </Box>

            {/* Tests on the script — not auto-merged; the admin opts in. */}
            <Box borderWidth="1px" borderColor="purple.100" bg="purple.50" borderRadius="md" p={3}>
              <Text fontSize="xs" fontWeight="700" color="purple.700" textTransform="uppercase" letterSpacing="wider" mb={2}>
                Tests on the Script
              </Text>
              <SimpleGrid columns={2} spacing={3} mb={3}>
                <Box>
                  <Text fontSize="2xs" color="gray.500" mb={1}>Keep #{keep.id} ({keepTests.length})</Text>
                  {keepTests.length ? (
                    <Wrap spacing={1}>
                      {keepTests.map((t, i) => (
                        <WrapItem key={`${t?.id}-${i}`}>
                          <Badge colorScheme="green" variant="subtle">{t?.name || t?.code || t?.id}</Badge>
                        </WrapItem>
                      ))}
                    </Wrap>
                  ) : (
                    <Text fontSize="sm" color="gray.400">—</Text>
                  )}
                </Box>
                <Box>
                  <Text fontSize="2xs" color="gray.500" mb={1}>From #{drop.id} ({dropTests.length})</Text>
                  {dropTests.length ? (
                    <Wrap spacing={1}>
                      {dropTests.map((t, i) => {
                        const isNew = newFromDrop.some((n) => String(n.id) === String(t?.id));
                        return (
                          <WrapItem key={`${t?.id}-${i}`}>
                            <Badge colorScheme={isNew ? 'blue' : 'gray'} variant={isNew ? 'solid' : 'subtle'}>
                              {t?.name || t?.code || t?.id}
                            </Badge>
                          </WrapItem>
                        );
                      })}
                    </Wrap>
                  ) : (
                    <Text fontSize="sm" color="gray.400">—</Text>
                  )}
                </Box>
              </SimpleGrid>
              {newFromDrop.length > 0 ? (
                <Checkbox colorScheme="purple" isChecked={mergeTests} onChange={(e) => setMergeTests(e.target.checked)}>
                  <Text fontSize="sm">
                    Add {newFromDrop.length} test{newFromDrop.length === 1 ? '' : 's'} from #{drop.id} to #{keep.id}
                  </Text>
                </Checkbox>
              ) : (
                <Text fontSize="xs" color="gray.500">
                  No new tests on #{drop.id} to add{dropTests.length ? ' (all already on #' + keep.id + ')' : ''}.
                </Text>
              )}
            </Box>

            {/* Order IDs — always combined onto the survivor for the phleb + admin. */}
            <Box borderWidth="1px" borderColor="blue.100" bg="blue.50" borderRadius="md" p={3}>
              <Text fontSize="xs" fontWeight="700" color="blue.700" textTransform="uppercase" letterSpacing="wider" mb={2}>
                Order ID
              </Text>
              <HStack spacing={3} fontSize="sm" flexWrap="wrap">
                <Text color="gray.600">Keep #{keep.id}: <b>{keep.lab_order_number || '—'}</b></Text>
                <Text color="gray.600">From #{drop.id}: <b>{drop.lab_order_number || '—'}</b></Text>
              </HStack>
              {gainsOrderId ? (
                <Text fontSize="sm" mt={2} color="blue.800">
                  Combined onto #{keep.id}: <Badge colorScheme="blue" fontFamily="mono">{combinedOrderIds}</Badge>
                </Text>
              ) : (
                <Text fontSize="xs" mt={1} color="gray.500">Nothing new to add — #{keep.id} keeps <b>{keep.lab_order_number || '—'}</b>.</Text>
              )}
              <Text fontSize="2xs" mt={1} color="gray.500">
                Status updates to the lab still go out under a single order (#{keep.id}'s lab connection); combining is for what the phleb &amp; admin see.
              </Text>
            </Box>

            {conflicts.length === 0 ? (
              <Text fontSize="sm" color="gray.500">No conflicting fields — nothing to choose. Just confirm the merge.</Text>
            ) : (
              <VStack align="stretch" spacing={3} divider={<Divider />}>
                {conflicts.map((f) => {
                  const pick = resolved[f] || 'primary';
                  return (
                    <Box key={f}>
                      <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={2}>
                        {prettyField(f)}
                      </Text>
                      <SimpleGrid columns={2} spacing={3}>
                        <Box
                          as="button"
                          textAlign="left"
                          onClick={() => choose(f, 'primary')}
                          p={3}
                          borderWidth="2px"
                          borderRadius="md"
                          borderColor={pick === 'primary' ? 'green.400' : 'gray.200'}
                          bg={pick === 'primary' ? 'green.50' : 'white'}
                        >
                          <Text fontSize="2xs" color="gray.500" mb={1}>Keep #{keep.id}</Text>
                          <Text fontSize="sm">{showVal(keep[f])}</Text>
                        </Box>
                        <Box
                          as="button"
                          textAlign="left"
                          onClick={() => choose(f, 'duplicate')}
                          p={3}
                          borderWidth="2px"
                          borderRadius="md"
                          borderColor={pick === 'duplicate' ? 'blue.400' : 'gray.200'}
                          bg={pick === 'duplicate' ? 'blue.50' : 'white'}
                        >
                          <Text fontSize="2xs" color="gray.500" mb={1}>From #{drop.id}</Text>
                          <Text fontSize="sm">{showVal(drop[f])}</Text>
                        </Box>
                      </SimpleGrid>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={saving}>Cancel</Button>
          <Flex>
            <Button colorScheme="purple" leftIcon={<FaExchangeAlt />} onClick={doMerge} isLoading={saving} loadingText="Merging…">
              Merge #{drop.id} → #{keep.id}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
